// Pure, framework-free mapping math for CanvasScrollbars. No React, no
// refs, no Yjs — every function here takes plain numbers and returns plain
// numbers/objects, so it's cheap to unit-test in isolation (see
// __tests__/scrollbarMath.test.js) and cheap to call from Canvas.jsx's hot
// paths without dragging any component/store dependency along.
//
// Vocabulary used throughout (per axis — call once for X, once for Y):
//   content{Min,Max}  — world-space bbox of every element on this axis, or
//                        null when there are no elements at all.
//   viewport{Min,Max} — world-space rect currently visible on screen,
//                        derived from originXY/scale/canvas size the same
//                        way Canvas.jsx's calculateVisibleElementsAndLines
//                        already does (viewportStart = (0-origin)/scale).
//   range{Min,Max}    — the scrollable extent the thumb travels across:
//                        union(content padded by `pad` on each side,
//                        current viewport) — see computeAxisRange.
//   thumbFrac  — thumb LENGTH as a fraction of the track (viewport/range,
//                        0..1), independent of any pixel size so the render
//                        layer can apply the min-thumb-length clamp against
//                        whatever the track's actual CSS pixel length is.
//   scrollFrac — where the viewport currently sits, normalized against the
//                        SCROLLABLE REMAINDER (rangeSize - viewportSize),
//                        NOT the full range: 0 = viewport at the range's
//                        start, 1 = viewport at the range's end. This is
//                        the fraction that stays correct after the
//                        min-thumb-length clamp changes how many pixels the
//                        thumb actually has to travel — see
//                        computeThumbGeometryPx/thumbDeltaPxToOriginDelta's
//                        module comments for why (thumbPosFrac normalized
//                        against the full range, an earlier version of this
//                        module, put a "dead zone" at the end of the track
//                        once a thumb got min-clamped: it visually pinned
//                        at the track edge while ~10-15% of range remained,
//                        AND a full-width drag under-covered the range by
//                        the same amount).

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// The minimum pixel length a thumb is ever allowed to shrink to, no matter
// how large `range` grows relative to the viewport.
export const MIN_THUMB_PX = 24;

// range = union(contentBounds padded by `pad` on EACH side, current
// viewport). No content -> range collapses to the viewport itself (a
// scrollbar over empty content has nothing to scroll to besides "here").
export function computeAxisRange({ contentMin, contentMax, viewportMin, viewportMax, pad = 0 }) {
    const hasContent = contentMin != null && contentMax != null;
    const paddedMin = hasContent ? contentMin - pad : viewportMin;
    const paddedMax = hasContent ? contentMax + pad : viewportMax;
    return {
        rangeMin: Math.min(paddedMin, viewportMin),
        rangeMax: Math.max(paddedMax, viewportMax),
    };
}

// A bar shows only when the viewport does NOT fully contain the content on
// this axis. Containment is inclusive (viewport exactly matching content
// counts as "fully contains" -> hidden). No content at all -> always false.
export function computeAxisVisibility({ contentMin, contentMax, viewportMin, viewportMax }) {
    if (contentMin == null || contentMax == null) return false;
    return !(viewportMin <= contentMin && viewportMax >= contentMax);
}

// Combines the two above into the shape CanvasScrollbars renders from.
// thumbFrac is a fraction of the track (0..1); scrollFrac is a fraction of
// the SCROLLABLE REMAINDER (see module comment) — computeThumbGeometryPx
// converts both to pixels and enforces MIN_THUMB_PX.
export function computeAxisScrollModel({ contentMin, contentMax, viewportMin, viewportMax, pad = 0 }) {
    const visible = computeAxisVisibility({ contentMin, contentMax, viewportMin, viewportMax });
    const { rangeMin, rangeMax } = computeAxisRange({ contentMin, contentMax, viewportMin, viewportMax, pad });
    const rangeSize = rangeMax - rangeMin;
    const viewportSize = viewportMax - viewportMin;

    if (!(rangeSize > 0)) {
        // Degenerate range (shouldn't happen in practice — range always
        // contains at least the viewport — but guards div-by-zero).
        return { visible, thumbFrac: 1, scrollFrac: 0 };
    }

    const thumbFrac = clamp(viewportSize / rangeSize, 0, 1);
    const scrollableRangeSize = rangeSize - viewportSize;
    const scrollFrac = scrollableRangeSize > 0
        ? clamp((viewportMin - rangeMin) / scrollableRangeSize, 0, 1)
        : 0;
    return { visible, thumbFrac, scrollFrac };
}

// Fractions -> track pixels, enforcing the minimum thumb length. Also
// returns maxPosPx (the thumb's ACTUAL pixel travel distance, trackLengthPx
// - lengthPx) — thumbDeltaPxToOriginDelta and trackClickToOrigin both need
// this exact value so drag/click math stays 1:1 with what's on screen even
// once the thumb has been min-clamped (posPx = scrollFrac * maxPosPx reaches
// precisely 0/maxPosPx at scrollFrac 0/1, unlike the old
// `scrollFrac * trackLengthPx` which overshot maxPosPx once lengthPx grew
// past what thumbFrac alone implied — see the module comment).
export function computeThumbGeometryPx({ thumbFrac, scrollFrac, trackLengthPx, minThumbPx = MIN_THUMB_PX }) {
    if (!(trackLengthPx > 0)) return { lengthPx: minThumbPx, posPx: 0, maxPosPx: 0 };
    const lengthPx = Math.min(trackLengthPx, Math.max(minThumbPx, thumbFrac * trackLengthPx));
    const maxPosPx = Math.max(0, trackLengthPx - lengthPx);
    const posPx = clamp(scrollFrac * maxPosPx, 0, maxPosPx);
    return { lengthPx, posPx, maxPosPx };
}

// Thumb-drag inverse mapping: a pixel delta the thumb just moved by ->
// the amount to ADD to originXY on that axis. Uses maxPosPx (the thumb's
// ACTUAL pixel travel — see computeThumbGeometryPx), NOT trackLengthPx, as
// the pixel side of the ratio, so a drag that pushes the thumb across its
// full physical travel distance covers exactly the full scrollable
// remainder — no dead zone (falling short) and no overshoot, matching
// computeThumbGeometryPx's posPx = scrollFrac * maxPosPx on the render
// side.
//
// Δworld = Δpx * scrollableRange/maxPosPx (pixel delta scaled from the
// thumb's travel space into world space), then originDelta = -Δworld *
// scale: dragging the thumb in the POSITIVE px direction moves the
// viewport in the positive world direction (you're scrolling further into
// the content, exactly like an OS scrollbar), which is the same on-screen
// effect as sliding the CONTENT in the NEGATIVE direction — hence the
// minus sign. Verified against Canvas.jsx's own transform
// (`viewportStart = (0 - originX) / scale`, so
// originX = -viewportMin * scale): increasing viewportMin decreases originX.
export function thumbDeltaPxToOriginDelta({ deltaPx, rangeMin, rangeMax, viewportMin, viewportMax, maxPosPx, scale }) {
    if (!(maxPosPx > 0) || !(scale > 0)) return 0;
    const scrollableRangeSize = (rangeMax - rangeMin) - (viewportMax - viewportMin);
    const deltaWorld = deltaPx * (scrollableRangeSize / maxPosPx);
    return -deltaWorld * scale;
}

// Track click (not on the thumb itself): jump so the thumb re-centers on
// the click position. Same maxPosPx-based normalization as the drag
// mapping above (clicking exactly at the track's end lands the viewport
// exactly at the range's end, even with a min-clamped thumb). Returns the
// ABSOLUTE origin value for that axis (not a delta — caller diffs against
// the current origin if it needs one, e.g. to feed the same delta-based
// pan primitive drag uses).
export function trackClickToOrigin({ clickPosPx, trackLengthPx, thumbLengthPx, rangeMin, rangeMax, viewportMin, viewportMax, scale }) {
    if (!(trackLengthPx > 0) || !(scale > 0)) return 0;
    const maxPosPx = Math.max(0, trackLengthPx - thumbLengthPx);
    const desiredPosPx = clamp(clickPosPx - thumbLengthPx / 2, 0, maxPosPx);
    const scrollFrac = maxPosPx > 0 ? desiredPosPx / maxPosPx : 0;
    const scrollableRangeSize = (rangeMax - rangeMin) - (viewportMax - viewportMin);
    const targetViewportMin = rangeMin + scrollFrac * scrollableRangeSize;
    return -targetViewportMin * scale;
}

// Hard pan limits (a later requirement than the scrollbar itself, sharing
// this module because the math is the same family — "how far can the
// viewport's world window get from content bounds"): the viewport's world
// window may extend at most `padFactor * viewportSizeWorld` beyond content
// on EACH side; a caller trying to pan further than that gets pinned
// instead. Per-axis — call once for X, once for Y, same as every other
// function in this module.
//
// Derivation, in origin space (origin = -viewportMin * scale, the same
// relation every other function here already uses):
//   viewportMin(origin) = -origin/scale
//   viewportMax(origin) = viewportMin(origin) + viewportSizeWorld
//   constraint: viewportMin >= contentMin - pad  AND  viewportMax <= contentMax + pad
// Solving each for origin gives:
//   originMax = (pad - contentMin) * scale            (from the viewportMin constraint)
//   originMin = (viewportSizeWorld - pad - contentMax) * scale  (from the viewportMax constraint)
// originMax - originMin = scale * (2*pad + contentSize - viewportSizeWorld), where
// contentSize = contentMax - contentMin. At padFactor=1 (pad =
// 1*viewportSizeWorld — one full viewport width/height of slack per side,
// the only value any call site in this codebase passes, see Canvas.jsx's
// PAN_LIMIT_FRACTION), 2*pad = 2*viewportSizeWorld, leaving
// scale*(viewportSizeWorld + contentSize) — strictly positive whenever
// viewportSizeWorld > 0, regardless of contentSize — this interval never
// inverts at the production padFactor. For a SMALLER padFactor (tested
// here for completeness, not because 1 ever reaches it — e.g. padFactor=0,
// "viewport must fully contain content" with zero slack) content-much-
// smaller-than-viewport CAN invert it — see the degenerate branch below.
export function clampOriginAxis({ desiredOrigin, contentMin, contentMax, viewportSizeWorld, scale, padFactor }) {
    if (contentMin == null || contentMax == null) return desiredOrigin; // no elements on this axis -> free pan
    if (!(scale > 0) || !(viewportSizeWorld > 0)) return desiredOrigin; // guard against a not-yet-measured viewport

    const pad = padFactor * viewportSizeWorld;
    let originMin = (viewportSizeWorld - pad - contentMax) * scale;
    let originMax = (pad - contentMin) * scale;

    if (originMin > originMax) {
        // Degenerate: content padded by `pad` on each side is narrower than
        // the viewport itself — there's no origin that satisfies "at most
        // `pad` of slack" on BOTH sides simultaneously. Fall back to the
        // constraint that matters more: keep the content bounds fully
        // within the viewport (viewportMin <= contentMin and viewportMax
        // >= contentMax), i.e. the padFactor=0 form of the same two
        // inequalities.
        originMin = -contentMin * scale;
        originMax = (viewportSizeWorld - contentMax) * scale;
        // This form is provably non-inverted whenever the branch above
        // triggered (viewportSizeWorld > contentSize is implied by that
        // inversion for any padFactor >= 0 — see the module comment's
        // derivation), but swap defensively rather than ever hand back an
        // inverted clamp for a pathological padFactor.
        if (originMin > originMax) {
            const swapped = originMin;
            originMin = originMax;
            originMax = swapped;
        }
    }

    return clamp(desiredOrigin, originMin, originMax);
}
